import SafeSummaryTile from "../../../components/common/SafeSummaryTile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { EmptyState } from "../../../components/common/EmptyState";
import { EmployeeSelect } from "../../../components/common/EmployeeSelect";
import { SafePageHeader, ConfirmDialog } from "../../../components/common/HelperFunc";
import { SummaryMetric } from "../../../components/common/SummaryMetric";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";
import { MRT_Localization_KO } from "../../../constants/localiztion";
import { FONT_PROPS } from "../../../styles/colors";
import { resolveEmployeeId } from "../../../utils/employeeIdentity";


const INSPECTION_STATUS = [
  { value: "normal", label: "특이없음", color: "success", icon: <CheckCircleIcon /> },
  { value: "action_required", label: "조치요청", color: "warning", icon: <ReportProblemIcon /> },
  { value: "action_done", label: "조치완료", color: "info", icon: <FactCheckIcon /> },
];

const SLA_STATUS = [
  { value: "tracking", label: "진행중", color: "warning" },
  { value: "overdue", label: "기한초과", color: "error" },
  { value: "done", label: "완료", color: "success" },
];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toLocalDateString = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end),
  };
};

const getDefaultDateTime = (date = getToday(), hour = "09:00") => `${date}T${hour}`;

const getInspectionStatus = (status) =>
  INSPECTION_STATUS.find((item) => item.value === status) || INSPECTION_STATUS[0];

const getSlaStatus = (status) =>
  SLA_STATUS.find((item) => item.value === status) || SLA_STATUS[0];

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const makeEmptyForm = (date = getToday(), inspectorEmpId = "") => ({
  location_id: "",
  inspector_emp_id: inspectorEmpId,
  inspected_at: getDefaultDateTime(date, "09:00"),
  content: "",
  request_title: "",
  request_note: "",
  status: "normal",
  action_title: "",
  assignee_emp_id: "",
  due_at: getDefaultDateTime(date, "18:00"),
  instruction: "",
  action_result: "",
  completed_at: "",
  sla_status: "tracking",
});

const ACTION_FORM_KEYS = new Set([
  "action_title",
  "assignee_emp_id",
  "due_at",
  "instruction",
  "action_result",
  "completed_at",
  "sla_status",
]);

export const buildEditForm = (row, date = getToday(), inspectorEmpId = "") => ({
  action_id: row.action_id || "",
  location_id: row.location_id || "",
  inspector_emp_id: row.inspector_emp_id ? String(row.inspector_emp_id) : inspectorEmpId,
  inspected_at: row.inspected_at_input || getDefaultDateTime(date, "09:00"),
  content: row.content || "",
  request_title: row.request_title ?? String(row.content || "").split(/\r?\n/)[0].slice(0, 200),
  request_note: row.request_note ?? (String(row.content || "").length > 200 || String(row.content || "").includes("\n") ? row.content : ""),
  status: row.status || "normal",
  action_title: row.action_title || "",
  assignee_emp_id: row.assignee_emp_id ? String(row.assignee_emp_id) : "",
  due_at: row.due_at_input || getDefaultDateTime(date, "18:00"),
  instruction: row.instruction || "",
  action_result: row.action_result || "",
  completed_at: row.completed_at_input || "",
  sla_status: row.effective_sla_status || row.sla_status || "tracking",
});

export const buildFormData = (form, photoFile, options = {}) => {
  const formData = new FormData();
  formData.append("request_only", "true");
  Object.entries(form).forEach(([key, value]) => {
    if (options.omitActionFields && ACTION_FORM_KEYS.has(key)) return;
    if (key === "content" && form.status !== "normal") {
      formData.append(key, [form.request_title, form.request_note].filter(Boolean).join("\n"));
      return;
    }
    formData.append(key, value ?? "");
  });
  if (photoFile) {
    formData.append("photo", photoFile);
  }
  return formData;
};

const INSPECTION_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="inspection-header"]',
    title: "화면 흐름",
    description:
      "기간과 상태를 기준으로 안전점검 기록을 조회하고, 점검 결과와 조치 진행 상태를 한 화면에서 확인합니다.",
  },
  {
    target: '[data-autopilot="inspection-filters"]',
    title: "조회 조건",
    description:
      "시작일, 종료일, 점검 상태를 바꾸면 조건에 맞는 일지만 다시 조회됩니다.",
  },
  {
    target: '[data-autopilot="inspection-create"]',
    title: "점검 등록",
    description:
      "모바일에서는 현장 근로자가 점검 결과와 사진을 남기고, 웹에서는 관리자가 조치 담당자와 기한을 배정합니다.",
  },
  {
    target: '[data-autopilot="inspection-summary"]',
    title: "요약 지표",
    description:
      "전체 점검 건수, 조치요청, 기한초과, 조치완료율을 빠르게 확인합니다. 오늘 처리해야 할 건을 먼저 파악하기 좋습니다.",
  },
  {
    target: '[data-autopilot="inspection-list"]',
    title: "일지 목록",
    description:
      "웹에서는 테이블로 검색, 선택 삭제, 수정이 가능하고 모바일에서는 카드 목록으로 핵심 정보와 수정 진입을 제공합니다.",
  },
  {
    target: '[data-autopilot="inspection-table-actions"]',
    title: "검색과 삭제",
    description:
      "웹 테이블에서는 점검자, 점검 내용, 조치 제목으로 검색하고 선택한 일지를 일괄 삭제할 수 있습니다. 모바일은 목록 조회와 수정에 집중합니다.",
  },
];

function AutoPilotGuide({ open, steps, stepIndex, onStepChange, onClose }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [targetRect, setTargetRect] = useState(null);
  const step = steps[stepIndex];

  useEffect(() => {
    if (!open || !step) return undefined;

    let rafId = null;
    const updateTargetRect = () => {
      const target = document.querySelector(step.target);
      if (!target) {
        setTargetRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      });
    };

    const target = document.querySelector(step.target);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    const scheduleUpdate = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateTargetRect);
    };

    const timeoutId = window.setTimeout(updateTargetRect, 120);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [open, step]);

  if (!open || !step) return null;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const panelWidth = 380;
  const panelSx =
    isSmall || !targetRect
      ? {
          left: 16,
          right: 16,
          bottom: 16,
        }
      : {
          width: panelWidth,
          left:
            targetRect.left + targetRect.width / 2 > viewportWidth / 2
              ? Math.max(16, targetRect.left - panelWidth - 18)
              : Math.min(viewportWidth - panelWidth - 16, targetRect.right + 18),
          top: Math.min(Math.max(16, targetRect.top), viewportHeight - 240),
        };

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 4,
          bgcolor: "rgba(15, 23, 42, 0.42)",
          pointerEvents: "none",
        }}
      />
      {targetRect ? (
        <Box
          sx={{
            position: "fixed",
            zIndex: (muiTheme) => muiTheme.zIndex.modal + 5,
            top: Math.max(8, targetRect.top - 8),
            left: Math.max(8, targetRect.left - 8),
            width: Math.min(viewportWidth - 16, targetRect.width + 16),
            height: Math.min(viewportHeight - 16, targetRect.height + 16),
            border: "2px solid #2563eb",
            borderRadius: 2,
            boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.18), 0 18px 48px rgba(15, 23, 42, 0.22)",
            pointerEvents: "none",
          }}
        />
      ) : null}
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 6,
          ...panelSx,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          pointerEvents: "auto",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Chip
              size="small"
              color="primary"
              icon={<PlayCircleOutlineIcon />}
              label={`오토파일럿 ${stepIndex + 1}/${steps.length}`}
            />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.65 }}>
              {step.description}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={((stepIndex + 1) / steps.length) * 100}
            sx={{ height: 6, borderRadius: 999 }}
          />
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              color="inherit"
              startIcon={<NavigateBeforeIcon />}
              disabled={isFirst}
              onClick={() => onStepChange(stepIndex - 1)}
            >
              이전
            </Button>
            <Button
              variant="contained"
              endIcon={isLast ? <CheckCircleIcon /> : <NavigateNextIcon />}
              onClick={() => {
                if (isLast) {
                  onClose();
                  return;
                }
                onStepChange(stepIndex + 1);
              }}
            >
              {isLast ? "완료" : "다음"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

function SummaryCard(props) { return <SafeSummaryTile {...props} />; }

function MobileSummaryBar({ summary, value, onChange }) {
  const items = [
    { key: "all", label: "전체", value: `${summary.total}건`, color: "#2563eb", icon: <FactCheckIcon fontSize="small" /> },
    { key: "action_required", label: "조치요청", value: `${summary.required}건`, color: "#f59e0b", icon: <ReportProblemIcon fontSize="small" /> },
    { key: "overdue", label: "기한초과", value: `${summary.overdue}건`, color: "#dc2626", icon: <AssignmentLateIcon fontSize="small" /> },
    { key: "action_done", label: "조치완료", value: `${summary.done}건`, color: "#16a34a", icon: <CheckCircleIcon fontSize="small" /> },
  ];

  return (
    <Paper
      data-autopilot="inspection-summary"
      elevation={0}
      sx={{
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Grid container spacing={0.75}>
        {items.map((item) => {
          const active = value === item.key;
          return (
          <Grid item xs={3} key={item.label}>
            <Stack
              spacing={0.4}
              alignItems="center"
              justifyContent="center"
              role="button"
              tabIndex={0}
              aria-pressed={active}
              onClick={() => onChange?.(item.key)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onChange?.(item.key);
              }}
              sx={{
                minHeight: 58,
                px: 0.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: active ? item.color : "transparent",
                bgcolor: active ? `${item.color}18` : `${item.color}0f`,
                color: item.color,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", lineHeight: 1 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1 }}>
                {item.value}
              </Typography>
            </Stack>
          </Grid>
        );
        })}
      </Grid>
    </Paper>
  );
}

export function InspectionFormDialog({
  open,
  mode,
  form,
  employees,
  photoFile,
  photoUrl = "",
  isMobile,
  isSaving,
  isLoadingDetail,
  inspectorMissing,
  contentInputRef,
  isListening,
  speechSupport,
  onClose,
  onChange,
  onPhotoChange,
  onVoiceInput,
  onSubmit,
}) {
  const isActionNeeded = form.status !== "normal";
  const isEdit = mode === "edit";
  const statusLabel = isMobile ? "점검 결과" : "점검 상태";
  const statusOptions = INSPECTION_STATUS.filter(item => item.value !== "action_done" || form.status === "action_done");
  const dialogTitle = isEdit
    ? isMobile
      ? "현장 안전점검 수정"
      : "안전점검 기록 수정"
    : isMobile
      ? "현장 안전점검 기록"
      : "안전점검 기록 등록";
  const submitLabel = isEdit ? "수정 완료" : isMobile ? "기록 저장" : "등록 완료";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 18px)", sm: 2 },
          pb: { xs: 1.5, sm: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: "#f7f8fb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
        }}
      >
        {inspectorMissing && !isLoadingDetail ? (
          <Alert severity={isMobile ? "warning" : "info"} sx={{ mb: 2, borderRadius: 2 }}>
            로그인 계정에 연결된 점검자가 없습니다. 실제 점검자를 선택해 주세요.
          </Alert>
        ) : null}

        {isLoadingDetail ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2.25}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "#fff",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 2 }}>점검 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="점검 일시"
                      value={form.inspected_at}
                      onChange={(event) => onChange("inspected_at", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                    <Grid item xs={12}>
                      <EmployeeSelect
                        employees={employees}
                        value={form.inspector_emp_id || ""}
                        onChange={(value) => onChange("inspector_emp_id", value)}
                        label="점검자 선택"
                        required
                        helperText="팀을 먼저 좁힌 뒤 이름이나 사번으로 검색할 수 있습니다."
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.75, ml: 1.5 }}
                      >
                        저장 시 선택한 직원이 점검자로 기록됩니다.
                      </Typography>
                    </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{statusLabel}</InputLabel>
                      <Select
                        label={statusLabel}
                        value={form.status}
                        disabled={Boolean(form.action_id)}
                        onChange={(event) => onChange("status", event.target.value)}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {isActionNeeded && <Grid item xs={12}>
                    <TextField fullWidth required label="조치 요청 제목"
                      placeholder="예: 프레스 2호기 바닥 누유 조치"
                      value={form.request_title || ""}
                      onChange={(event) => onChange("request_title", event.target.value)}
                      inputProps={{ maxLength: 200 }} />
                  </Grid>}
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                      <Typography component="label" htmlFor="inspection-note" variant="body2">
                        {isActionNeeded ? "조치 요청 메모 (선택)" : "점검 내용"}
                      </Typography>
                      <Tooltip title={speechSupport.title}>
                        <Button size="small" color={isListening ? "primary" : "inherit"}
                          startIcon={<KeyboardVoiceIcon fontSize="small" />} onClick={onVoiceInput}
                          disabled={isSaving || isListening}
                          aria-label={isActionNeeded ? "조치 요청 메모 음성 입력" : "점검 내용 음성 입력"}
                          sx={{ flexShrink: 0, minHeight: 36 }}>
                          {isListening ? "듣는 중" : "음성 입력"}
                        </Button>
                      </Tooltip>
                    </Stack>
                    <TextField id="inspection-note" inputRef={contentInputRef}
                      fullWidth multiline minRows={3}
                      placeholder={isActionNeeded ? "위치나 현장 상황 등 전달할 내용을 적어주세요." : "예: 프레스 2호기 주변 바닥 오일 누유 발견"}
                      value={(isActionNeeded ? form.request_note : form.content) || ""}
                      onChange={(event) => onChange(isActionNeeded ? "request_note" : "content", event.target.value)}
                      inputProps={{ maxLength: isActionNeeded ? 10000 : undefined }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, ml: 1.5 }}>
                      {speechSupport.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ImageUploadBox
                      label="현장 사진"
                      file={photoFile}
                      fileUrl={resolvePhotoUrl(photoUrl)}
                      onFileChange={onPhotoChange}
                      capture="environment"
                      compact={isMobile}
                      helperText={
                        isMobile
                          ? "사진은 선택 사항이며, 큰 이미지는 자동으로 줄어듭니다."
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {isActionNeeded && <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {form.action_id ? `조치 상태: ${form.status === 'action_done' ? '완료' : '처리 중'} · ${form.action_title}` : '담당자 배정 대기'}
              </Typography>
            </Grid>}
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-end" },
          borderTop: { xs: "1px solid", sm: "none" },
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          취소
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isSaving || isLoadingDetail}
          startIcon={isSaving ? null : <FactCheckIcon />}
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          {isSaving ? "저장 중" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MobileInspectionCard({ row, onEdit, onDelete, isDeleting = false }) {
  const status = getInspectionStatus(row.status);
  const sla = row.action_id ? getSlaStatus(row.effective_sla_status || row.sla_status) : null;

  return (
    <Card className="safe-mobile-record" variant="outlined" sx={{ borderRadius: 1 }}>
      <CardContent>
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900 }}>
                {row.inspected_day} {row.inspected_time}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.location_path || row.location_name || "현장 점검"}
              </Typography>
            </Box>
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {row.content || "점검 내용 없음"}
          </Typography>

          {row.action_id ? (
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: "#fff8e1" }}>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 900, minWidth: 0, overflowWrap: "anywhere" }}>
                    {row.action_title}
                  </Typography>
                  <Chip size="small" color={sla.color} label={sla.label} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  담당 {row.assignee_name || "-"} · 기한 {row.due_day || "-"} {row.due_time || ""}
                </Typography>
              </Stack>
            </Paper>
          ) : null}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" icon={<GroupsIcon />} label={row.inspector_department || "부서 미등록"} />
            {row.photo_url ? (
              <Chip
                size="small"
                icon={<CameraAltIcon />}
                label="사진"
                component="a"
                href={resolvePhotoUrl(row.photo_url)}
                target="_blank"
                clickable
                onClick={(event) => event.stopPropagation()}
              />
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={isDeleting} onClick={() => onDelete(row)}>
          삭제
        </Button>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(row)}>
          수정
        </Button>
      </CardActions>
    </Card>
  );
}

export function SafetyInspectionManagement() {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const user = useSelector((state) => state.auth.user);
  const queryEmpId = searchParams.get("emp_id") || "";

  const defaultRange = useMemo(() => getMonthRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isContentListening, setIsContentListening] = useState(false);
  const contentInputRef = useRef(null);

  const speechSupport = useMemo(() => {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAppleMobile = /iPad|iPhone|iPod/i.test(userAgent);
    const supportsBrowserSpeech =
      typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

    if (isAppleMobile && !supportsBrowserSpeech) {
      return {
        supported: false,
        title: "iPhone/iPad 음성 입력",
        description: "입력칸을 선택한 뒤 키보드 하단의 마이크 버튼으로 점검 내용을 입력할 수 있습니다.",
      };
    }

    if (!supportsBrowserSpeech) {
      return {
        supported: false,
        title: "기기 키보드 음성 입력",
        description: "브라우저 직접 음성인식은 지원되지 않습니다. 입력칸을 선택한 뒤 키보드 마이크를 사용해주세요.",
      };
    }

    return {
      supported: true,
      title: "브라우저 음성 입력",
      description: "마이크 버튼을 누른 뒤 점검 내용을 말하면 입력칸에 자동으로 추가됩니다.",
    };
  }, []);

  const optionsQuery = useQuery({
    queryKey: ["risk-inspection-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/inspection/options");
      return response.data || { employees: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [] },
  });

  const inspectionsQuery = useQuery({
    queryKey: ["risk-inspection-logs", group_code, project_code, startDate, endDate],
    queryFn: async () => {
      const response = await api.get("/inspection/logs", {
        params: {
          start: startDate,
          end: endDate,
        },
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && startDate && endDate),
    placeholderData: [],
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const resolvedInspectorEmpId = useMemo(
    () => resolveEmployeeId({ user, queryEmpId, employees }),
    [employees, queryEmpId, user]
  );

  useEffect(() => {
    if (!resolvedInspectorEmpId) return;
    setForm((prev) =>
      prev.inspector_emp_id ? prev : { ...prev, inspector_emp_id: resolvedInspectorEmpId }
    );
  }, [resolvedInspectorEmpId]);

  const invalidateLogs = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-inspection-logs", group_code, project_code, startDate, endDate],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormData(form, photoFile, { omitActionFields: true });
      if (dialogMode === "edit") {
        return api.put(`/inspection/logs/${editingId}`, payload);
      }
      return api.post("/inspection/logs", payload);
    },
    onSuccess: () => {
      toast.success(dialogMode === "edit" ? "안전점검 기록이 저장되었습니다." : "안전점검 기록이 등록되었습니다.");
      closeDialog();
      invalidateLogs();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "안전점검 기록 저장 중 오류가 발생했습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/inspection/logs", { data: { ids } }),
    onSuccess: () => {
      toast.success("선택한 안전점검 기록이 삭제되었습니다.");
      setDeleteTargets([]);
      setRowSelection({});
      invalidateLogs();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "안전점검 기록 삭제 중 오류가 발생했습니다.");
    },
  });

  const rawRows = useMemo(() => inspectionsQuery.data || [], [inspectionsQuery.data]);

  const rows = useMemo(() => {
    if (statusFilter === "all") return rawRows;
    if (statusFilter === "overdue") {
      return rawRows.filter((row) => (row.effective_sla_status || row.sla_status) === "overdue");
    }
    return rawRows.filter((row) => row.status === statusFilter);
  }, [rawRows, statusFilter]);

  const summary = useMemo(() => {
    const normal = rawRows.filter((row) => row.status === "normal").length;
    const required = rawRows.filter((row) => row.status === "action_required").length;
    const done = rawRows.filter((row) => row.status === "action_done").length;
    const overdue = rawRows.filter(
      (row) => (row.effective_sla_status || row.sla_status) === "overdue"
    ).length;
    const actionTotal = rawRows.filter((row) => row.action_id).length;
    const doneRate = actionTotal ? Math.round((done / actionTotal) * 100) : 0;
    return { total: rawRows.length, normal, required, done, overdue, actionTotal, doneRate };
  }, [rawRows]);

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => Number(key))
        .filter(Boolean),
    [rowSelection]
  );

  const autopilotSteps = useMemo(
    () =>
      isMobile
        ? INSPECTION_AUTOPILOT_STEPS.filter(
            (step) => step.target !== '[data-autopilot="inspection-table-actions"]'
          )
        : INSPECTION_AUTOPILOT_STEPS,
    [isMobile]
  );

  useEffect(() => {
    if (autopilotStepIndex >= autopilotSteps.length) {
      setAutopilotStepIndex(Math.max(0, autopilotSteps.length - 1));
    }
  }, [autopilotStepIndex, autopilotSteps.length]);

  const openAutopilot = () => {
    if (isMobile) setMobileFiltersOpen(true);
    setAutopilotStepIndex(0);
    setAutopilotOpen(true);
  };

  const openCreateDialog = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(makeEmptyForm(getToday(), resolvedInspectorEmpId));
    setPhotoFile(null);
    setPhotoUrl("");
    setDialogOpen(true);
  };

  useEffect(() => {
    const proposal = routeLocation.state?.followUpProposal;
    if (!proposal) return;
    const content = `[위험요인 제안 #${proposal.proposal_id}] ${proposal.approved_danger_description || proposal.proposal_text || ''}`;
    setDialogMode('create');
    setEditingId(null);
    setPhotoFile(null);
    setPhotoUrl("");
    setForm({ ...makeEmptyForm(getToday(), resolvedInspectorEmpId),
      location_id: proposal.location_id || '', content, status: 'action_required',
      action_title: String(proposal.approved_danger_description || proposal.proposal_text || '위험요인 후속 조치').slice(0, 100),
      instruction: content,
    });
    setDialogOpen(true);
    navigate(routeLocation.pathname, { replace: true, state: null });
  }, [routeLocation.state, routeLocation.pathname, navigate, resolvedInspectorEmpId]);

  const openEditDialog = async (row) => {
    setDialogMode("edit");
    setEditingId(row.id);
    setDialogOpen(true);
    setPhotoFile(null);
    setPhotoUrl(row.photo_url || "");
    setIsLoadingDetail(true);

    try {
      const response = await api.get(`/inspection/logs/${row.id}`);
      setForm(buildEditForm(response.data || row, row.inspected_day, resolvedInspectorEmpId));
      setPhotoUrl((response.data || row).photo_url || "");
    } catch (error) {
      toast.error(error?.response?.data?.message || "안전점검 상세 조회 중 오류가 발생했습니다.");
      setForm(buildEditForm(row, row.inspected_day, resolvedInspectorEmpId));
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setEditingId(null);
    setPhotoFile(null);
    setPhotoUrl("");
    setForm(makeEmptyForm(getToday(), resolvedInspectorEmpId));
    setIsLoadingDetail(false);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => {
      if (key === "status" && prev.status === "normal" && value === "action_required") {
        return { ...prev, status: value, request_note: prev.request_note || prev.content };
      }
      if (key === "status" && value === "action_done" && prev.sla_status !== "done") {
        return { ...prev, [key]: value, sla_status: "done" };
      }
      if (key === "completed_at" && value) {
        return { ...prev, [key]: value, sla_status: "done", status: "action_done" };
      }
      return { ...prev, [key]: value };
    });
  };

  const appendContentText = (text) => {
    setForm(prev => {
      const key = prev.status === "normal" ? "content" : "request_note";
      return { ...prev, [key]: prev[key] ? `${prev[key]}\n${text}` : text };
    });
  };

  const startContentVoiceInput = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      contentInputRef.current?.focus();
      toast.info(speechSupport.description);
      return;
    }

    const recognition = new Recognition();
    recognition.lang = "ko-KR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setIsContentListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      if (transcript) appendContentText(transcript);
    };
    recognition.onerror = () => {
      toast.warning("음성을 인식하지 못했습니다. 다시 시도해주세요.");
    };
    recognition.onend = () => {
      setIsContentListening(false);
    };
    recognition.start();
  };

  const handleSubmit = () => {
    if (!form.inspector_emp_id) {
      toast.warning(isMobile ? "점검자 정보가 연결되지 않았습니다." : "점검자를 선택해주세요.");
      return;
    }
    if (!form.inspected_at) {
      toast.warning("점검 일시를 입력해주세요.");
      return;
    }
    if (form.status !== "normal" && !String(form.request_title || "").trim()) {
      toast.warning("조치 요청 제목을 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const handleDelete = () => {
    if (!selectedIds.length) {
      toast.warning("삭제할 안전점검 기록을 선택해주세요.");
      return;
    }
    setDeleteTargets(rows.filter((row) => selectedIds.includes(Number(row.id))));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "inspected_at_input",
        header: "점검 일시",
        size: 150,
        Cell: ({ row }) => (
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 900 }}>{row.original.inspected_day}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.inspected_time}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "inspector_name",
        header: "점검자",
        size: 150,
        Cell: ({ row }) => (
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 800 }}>{row.original.inspector_name || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.inspector_department || "부서 미등록"}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "content",
        header: "점검 내용",
        size: 280,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            title={cell.getValue() || ""}
            sx={{
              maxWidth: 320,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {cell.getValue() || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "상태",
        size: 120,
        Cell: ({ cell }) => {
          const status = getInspectionStatus(cell.getValue());
          return <Chip size="small" color={status.color} label={status.label} />;
        },
      },
      {
        accessorKey: "action_title",
        header: "조치",
        size: 260,
        Cell: ({ row }) => {
          if (!row.original.action_id) return <Typography color="text.secondary">-</Typography>;
          const sla = getSlaStatus(row.original.effective_sla_status || row.original.sla_status);
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {row.original.action_title}
                </Typography>
                <Chip size="small" color={sla.color} label={sla.label} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                담당 {row.original.assignee_name || "-"} · 기한 {row.original.due_day || "-"} {row.original.due_time || ""}
              </Typography>
            </Stack>
          );
        },
      },
      {
        accessorKey: "photo_url",
        header: "사진",
        size: 100,
        Cell: ({ cell }) => {
          const photoUrl = cell.getValue();
          if (!photoUrl) return <Typography color="text.secondary">-</Typography>;
          return (
            <Button
              size="small"
              href={resolvePhotoUrl(photoUrl)}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              보기
            </Button>
          );
        },
      },
    ],
    []
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        type="date"
        label="시작일"
        value={startDate}
        onChange={(event) => setStartDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        type="date"
        label="종료일"
        value={endDate}
        onChange={(event) => setEndDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>상태</InputLabel>
        <Select
          label="상태"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setRowSelection({});
          }}
        >
          <MenuItem value="all">전체</MenuItem>
          {INSPECTION_STATUS.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
          <MenuItem value="overdue">기한초과</MenuItem>
        </Select>
      </FormControl>
    </>
  );

  return (
    <Box className="safe-page-root"
      sx={{
        p: { xs: 1.25, sm: 1.5, lg: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom))", lg: 3 },
        height: "100%",
        overflow: isMobile ? "auto" : "hidden",
        overflowX: "hidden",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack className="safe-page-stack"
        spacing={{ xs: 1.5, lg: 2.5 }}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxWidth: { xs: "100%", xl: 1540 },
          mx: "auto",
        }}
      >
        <SafePageHeader title="현장 안전점검" data-autopilot="inspection-header" actions={<Stack direction="row" spacing={1}><Tooltip title="오토파일럿">
                  <IconButton
                    color="primary"
                    onClick={openAutopilot}
                    sx={{
                      width: 40,
                      height: 40,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "#fff",
                      "&:hover": {
                        bgcolor: "primary.lightOpacity",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <PlayCircleOutlineIcon />
                  </IconButton>
                </Tooltip><Button
                  data-autopilot="inspection-create"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                >
                  등록
                </Button></Stack>}></SafePageHeader>
        {isMobile ? (
          <Box className="safe-page-toolbar">
            <Button
              color="inherit"
              startIcon={<FilterListIcon />}
              aria-expanded={mobileFiltersOpen}
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
            >
              {mobileFiltersOpen ? "조회 조건 접기" : "조회 조건 보기"}
            </Button>
            <Collapse in={mobileFiltersOpen} timeout="auto" unmountOnExit>
              <Stack data-autopilot="inspection-filters" spacing={1} sx={{ pt: 1 }}>
                {filterControls}
              </Stack>
            </Collapse>
          </Box>
        ) : null}

        {isMobile ? (
          <MobileSummaryBar
            summary={summary}
            value={statusFilter}
            onChange={(nextFilter) => {
              setStatusFilter(nextFilter);
              setRowSelection({});
            }}
          />
        ) : (
          <Box className="safe-inspection-overview">
            <Box className="safe-inspection-filters" data-autopilot="inspection-filters">
              {filterControls}
            </Box>
            <Box className="safe-inspection-summary" data-autopilot="inspection-summary">
            <Box>
              <SummaryCard
                icon={<FactCheckIcon />}
                label="전체"
                value={`${summary.total}건`}
                active={statusFilter === "all"}
                onClick={() => {
                  setStatusFilter("all");
                  setRowSelection({});
                }}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<CheckCircleIcon />}
                label="특이없음"
                value={`${summary.normal}건`}
                color="#16a34a"
                active={statusFilter === "normal"}
                onClick={() => {
                  setStatusFilter("normal");
                  setRowSelection({});
                }}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<ReportProblemIcon />}
                label="조치요청"
                value={`${summary.required}건`}
                color="#f59e0b"
                active={statusFilter === "action_required"}
                onClick={() => {
                  setStatusFilter("action_required");
                  setRowSelection({});
                }}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<AssignmentLateIcon />}
                label="기한초과"
                value={`${summary.overdue}건`}
                color="#dc2626"
                active={statusFilter === "overdue"}
                onClick={() => {
                  setStatusFilter("overdue");
                  setRowSelection({});
                }}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<FactCheckIcon />}
                label="조치완료"
                value={`${summary.done}건`}
                color="#0284c7"
                active={statusFilter === "action_done"}
                onClick={() => {
                  setStatusFilter("action_done");
                  setRowSelection({});
                }}
              />
            </Box>
            <Box>
              <SummaryCard
                icon={<GroupsIcon />}
                label="조치 완료율"
                value={`${summary.doneRate}%`}
                color="#16a34a"
              />
            </Box>
            </Box>
          </Box>
        )}

        {inspectionsQuery.isError ? (
          <Alert severity="error">안전점검 기록을 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        {isMobile ? (
          <Stack data-autopilot="inspection-list" spacing={1.25}>
            {inspectionsQuery.isLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : null}
            {rows.map((row) => (
              <MobileInspectionCard key={row.id} row={row} onEdit={openEditDialog} onDelete={(target) => setDeleteTargets([target])} isDeleting={deleteMutation.isPending} />
            ))}
            {!inspectionsQuery.isLoading && !rows.length ? (
              <EmptyState
                compact
                icon={<FactCheckIcon />}
                title="조회된 안전점검 기록이 없습니다"
                description="선택한 기간과 상태 조건에 맞는 점검 기록이 없습니다. 현장 점검 후 기록을 남기면 관리자가 조치 여부를 확인할 수 있습니다."
                actionLabel={isMobile ? "안전점검 기록" : "점검 일지 등록"}
                actionIcon={<AddIcon />}
                onAction={openCreateDialog}
              />
            ) : null}
          </Stack>
        ) : (
          <Paper
            data-autopilot="inspection-list"
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <MaterialReactTable
              columns={columns}
              data={rows}
              localization={MRT_Localization_KO}
              getRowId={(row) => String(row.id)}
              enableRowSelection
              enableMultiRowSelection
              enableColumnActions={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnFilters={false}
              enableStickyHeader
              state={{
                isLoading: inspectionsQuery.isLoading,
                showProgressBars: inspectionsQuery.isFetching,
                globalFilter,
                rowSelection,
              }}
              onGlobalFilterChange={setGlobalFilter}
              onRowSelectionChange={setRowSelection}
              muiTablePaperProps={{
                sx: {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#fff",
                  boxShadow: "none",
                  "& *": {
                    fontFamily: `${FONT_PROPS.PRIMARY}, sans-serif !important`,
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "none",
                  bgcolor: "#fff",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  bgcolor: "#fff",
                  borderTop: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiTableBodyProps={{
                sx: {
                  bgcolor: "#fff",
                },
              }}
              muiTableBodyRowProps={{
                sx: {
                  bgcolor: "#fff",
                  "&:hover td": {
                    bgcolor: "#fff",
                  },
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  fontWeight: 900,
                  bgcolor: "#f1f5f9",
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack
                  data-autopilot="inspection-table-actions"
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ p: 1, width: "100%" }}
                >
                  <TextField
                    size="small"
                    placeholder="점검자, 내용, 조치 검색"
                    value={globalFilter || ""}
                    onChange={(event) => setGlobalFilter(event.target.value)}
                    sx={{ width: { xs: "100%", md: 320 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Divider orientation="vertical" flexItem />
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={!selectedIds.length || deleteMutation.isPending}
                      onClick={handleDelete}
                    >
                      삭제
                    </Button>
                </Stack>
              )}
              enableRowActions
              positionActionsColumn="last"
              renderRowActions={({ row }) => (
                <Tooltip title="수정">
                  <IconButton onClick={() => openEditDialog(row.original)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              renderEmptyRowsFallback={() => (
                <EmptyState
                  compact
                  icon={<FactCheckIcon />}
                  title="조회된 안전점검 기록이 없습니다"
                  description="기간이나 상태 필터를 변경하거나 새 점검 일지를 등록해보세요."
                  actionLabel="점검 일지 등록"
                  actionIcon={<AddIcon />}
                  onAction={openCreateDialog}
                />
              )}
            />
          </Paper>
        )}
      </Stack>

      <AutoPilotGuide
        open={autopilotOpen}
        steps={autopilotSteps}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />

      <InspectionFormDialog
        open={dialogOpen}
        mode={dialogMode}
        form={form}
        employees={employees}
        photoFile={photoFile}
        photoUrl={photoUrl}
        isMobile={isMobile}
        isSaving={saveMutation.isPending}
        isLoadingDetail={isLoadingDetail}
        inspectorMissing={!form.inspector_emp_id}
        contentInputRef={contentInputRef}
        isListening={isContentListening}
        speechSupport={speechSupport}
        onClose={closeDialog}
        onChange={handleFormChange}
        onPhotoChange={setPhotoFile}
        onVoiceInput={startContentVoiceInput}
        onSubmit={handleSubmit}
      />
      <ConfirmDialog
        open={deleteTargets.length > 0}
        title="안전점검 기록 삭제"
        message={`${deleteTargets.length}건의 점검 기록, 연결된 조치와 사진을 삭제할까요?`}
        pending={deleteMutation.isPending}
        confirmLabel="삭제"
        onCancel={() => setDeleteTargets([])}
        onConfirm={() => { if (!deleteMutation.isPending) deleteMutation.mutate(deleteTargets.map((row) => row.id)); }}
      >
        {deleteTargets.map((row) => <Typography key={row.id} variant="body2" sx={{ mt: 1, overflowWrap: "anywhere" }}>{row.inspected_day} {row.content}</Typography>)}
      </ConfirmDialog>
    </Box>
  );
}
